import { GridColDef } from '@mui/x-data-grid'

import { getUserType, getUserTypeDisplay } from 'components/SegmentSelector/UserTypeSelector'
import {
  getIsOneTimeMessage,
  getIsUsingManualStudentSelection,
  getIsUsingMyMatches,
} from 'components/SegmentSelector/segment-service'

import { Message } from '../types'
import Actions from './Actions'
import MessageCell from './MessageCell'
import StatusCell from './StatusCell'
import TriggerCell from './TriggerCell'
import { onEnableMessage } from './message-actions'

interface ExtendedGridColDef extends GridColDef<Message> {
  omit?: boolean
}

type MessageTableColumnsProps = {
  route: 'automated' | 'one-time'
  onChange: () => void
  onDelete: (id: string) => void
  dispatch: any
  studentOutreachEnabledOrShadow: boolean
}

const getColumns = ({
  route,
  onChange,
  onDelete,
  dispatch,
  studentOutreachEnabledOrShadow,
}: MessageTableColumnsProps) => {
  const columns: ExtendedGridColDef[] = [
    {
      field: 'CreatedDate',
      headerName: 'Date Created',
      valueGetter: (params) => {
        return new Intl.DateTimeFormat('en-US', {
          dateStyle: 'medium',
        }).format(new Date(params.row.CreatedDate))
      },
      sortComparator: (v1, v2) => {
        return sortDates(v1, v2)
      },
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'ScheduledDate',
      headerName: 'Send Date',
      omit: route === 'automated',
      valueGetter: (params) => {
        return new Intl.DateTimeFormat('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(params.row.ScheduledDate || params.row.CreatedDate))
      },
      sortComparator: (v1, v2) => {
        return sortDates(v1, v2)
      },
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'Status',
      headerName: 'Status',
      valueGetter: (params) => {
        let status = params.row.IsDraft ? 'Draft' : params.row.Status || 'Delivered'
        if (status === 'Delivered' && route !== 'one-time') {
          status = 'Live'
        }
        if (params.row.Disabled) {
          status = 'Disabled'
        }
        return status
      },
      renderCell: (params) => {
        return <StatusCell status={params.value} />
      },
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'Message',
      headerName: 'Message',
      renderCell: (params) => {
        return <MessageCell data={params.row} />
      },
      flex: 3,
      minWidth: 200,
    },
    {
      field: 'Trigger',
      headerName: 'Trigger',
      omit: route === 'one-time',
      flex: 1,
      renderCell: (params) => {
        return (
          <TriggerCell
            trigger={params.value}
            priority={params.row.Priority}
            supportsStudentOutreach={studentOutreachEnabledOrShadow}
          />
        )
      },
      minWidth: 150,
    },
    {
      field: 'Audience',
      headerName: 'Audience',
      valueGetter: (params) => {
        return getAudienceDisplay({ segment: params.row.Segment, trigger: params.row.Trigger })
      },
      flex: 1,
    },
    {
      field: 'TotalSent',
      headerName: 'Total Sent',
      flex: 1,
    },
    {
      field: 'TotalOpened',
      headerName: 'Opens',
      flex: 1,
    },
    {
      field: 'OpenRate',
      headerName: 'Open Rate',
      valueGetter: (params) => {
        return Math.floor((params.row.TotalOpened * 100) / Math.max(params.row.TotalSent, 1))
      },
      renderCell: (params) => {
        return `${params.value}%`
      },
      description: 'Percentage of delivered messages that were opened at least once.',
      flex: 1,
    },
    {
      field: 'TotalClicked',
      headerName: 'Clicks',
      flex: 1,
    },
    {
      field: 'ClickThroughRate',
      headerName: 'Click Through Rate',
      valueGetter: (params) => {
        return Math.floor((params.row.TotalClicked * 100) / Math.max(params.row.TotalOpened, 1))
      },
      renderCell: (params) => {
        return `${params.value}%`
      },
      description: 'Percentage of opened messages where at least one link was clicked.',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: (params) => {
        return (
          <Actions
            canModifyMessages={studentOutreachEnabledOrShadow}
            record={params.row}
            onDelete={() => {
              onDelete(params.row.Id)
            }}
            onEnable={() => {
              onEnableMessage({ message: params.row, enabled: true, onChange, dispatch })
            }}
            onDisable={() => {
              onEnableMessage({ message: params.row, enabled: false, onChange, dispatch })
            }}
          />
        )
      },
      width: 100,
    },
  ]

  const filteredColumns = columns.filter((column) => !column.omit)
  return filteredColumns
}

export const getAudienceDisplay = ({
  segment,
  trigger,
}: {
  segment: Message['Segment']
  trigger: Message['Trigger']
}) => {
  const isOneTimeMessage = getIsOneTimeMessage(trigger)
  if (!isOneTimeMessage) {
    return getUserTypeDisplay(segment)
  }

  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const audience = isUsingManualStudentSelection ? 'IDs' : isUsingMyMatches ? 'Matches' : 'Followers'
  const userType = getUserType(segment)
  return `${userType} ${audience}`
}

const sortDates = (date1: string, date2: string) => {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  return d1 > d2 ? 1 : d1 < d2 ? -1 : 0
}

export default getColumns
